/**
 * listingDiscounts.js — Available Discounts presets (Listing Richness release, A5)
 *
 * Owners pick discount TYPES from a fixed preset dropdown (multi-select, Haul Star
 * only — the input UI is built in Phase B). Each selection is stored on the listing
 * as `publicData.discounts` = an array of preset KEYS (e.g. ['weekly','first_time']).
 * This module maps those keys to a display label + icon for the PDP hero + search-card
 * image OVERLAY pills.
 *
 * COMPLIANCE (locked 2026-09-27): these are PURE DISPLAY / PURE CTA. Pills say
 * "available" (= ask the owner), never amounts / codes / specifics. BigLoads does
 * NOT honor, transact, or redeem any of it — the renter mentions it when they contact
 * the owner directly. Zero liability. Unknown keys are dropped (never rendered raw).
 *
 * Preset list is tunable; keep keys stable once live (they persist on listings).
 */

// Preset key -> { label, icon }. `icon` is a key in components/Icons/Icons.js.
// A single shared price-tag icon keeps the overlay pills visually consistent.
export const DISCOUNT_PRESETS = {
  weekly: { label: 'Weekly discount available', icon: 'discountTag' },
  monthly: { label: 'Monthly discount available', icon: 'discountTag' },
  first_time: { label: 'First-time renter discount', icon: 'discountTag' },
  multi_trailer: { label: 'Multi-trailer discount', icon: 'discountTag' },
  long_term: { label: 'Long-term rental discount', icon: 'discountTag' },
  repeat_renter: { label: 'Repeat renter discount', icon: 'discountTag' },
};

// Order presets render in when an owner selects several (stable, not selection order).
export const DISCOUNT_PRESET_ORDER = [
  'weekly',
  'monthly',
  'first_time',
  'multi_trailer',
  'long_term',
  'repeat_renter',
];

// Max pills to show on an image overlay so we never crowd the photo / Haul Star badge.
export const MAX_DISCOUNT_PILLS = 3;

/**
 * Normalize a listing's raw `publicData.discounts` into a clean, capped, ordered
 * array of renderable pills. Accepts an array of preset keys (strings). Silently
 * drops anything not in DISCOUNT_PRESETS (forward-compat + no raw/unknown keys leak).
 *
 * @param {*} raw - expected string[] of preset keys
 * @param {number} [cap=MAX_DISCOUNT_PILLS]
 * @returns {Array<{ key: string, label: string, icon: string }>}
 */
export const getDiscountPills = (raw, cap = MAX_DISCOUNT_PILLS) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const seen = new Set();
  const selected = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const key = item.trim().toLowerCase();
    if (!DISCOUNT_PRESETS[key] || seen.has(key)) continue;
    seen.add(key);
    selected.push(key);
  }
  if (selected.length === 0) return [];

  // Render in the stable preset order (not the owner's selection order).
  const ordered = DISCOUNT_PRESET_ORDER.filter(k => seen.has(k));
  return ordered.slice(0, cap).map(key => ({
    key,
    label: DISCOUNT_PRESETS[key].label,
    icon: DISCOUNT_PRESETS[key].icon,
  }));
};

/**
 * Full (UNCAPPED) list of a listing's discount pills, stable-ordered. Used for
 * the PDP "Discounts Available" body text section which lists ALL of them (the
 * capped `getDiscountPills` is for image overlays / the search card badge era).
 *
 * @param {*} raw - expected string[] of preset keys
 * @returns {Array<{ key: string, label: string, icon: string }>}
 */
export const getAllDiscountPills = raw => getDiscountPills(raw, Infinity);

/**
 * True if a listing has at least one recognized discount preset selected. Drives
 * the single generic "Discounts Available" badge on the search/feed card (we no
 * longer stack individual discount pills on the card image — the itemized list
 * lives in the PDP body).
 *
 * @param {*} raw - expected string[] of preset keys
 * @returns {boolean}
 */
export const hasDiscounts = raw => getDiscountPills(raw, Infinity).length > 0;
